import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import FlowireAPI from '../../flowireapi/FlowireAPI';
import Plot from 'react-plotly.js';
import Select from 'react-select';
import {
    Box,
    VStack,
    HStack,
    Input,
    Button,
    Text,
    Heading,
    Alert,
    AlertIcon,
    FormControl,
    FormLabel,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    useColorModeValue,
    Badge
} from '@chakra-ui/react';

const Assumptions = () => {
    const { getIdTokenClaims, isAuthenticated } = useAuth0();
    
    // User assumption inputs
    const [electricityCost, setElectricityCost] = useState(70);
    const [interestRate, setInterestRate] = useState(6);
    const [years, setYears] = useState(20);
    
    // Model selection
    const [groups, setGroups] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);
    
    // Results
    const [originalNpvData, setOriginalNpvData] = useState([]);
    const [npvData, setNpvData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingOriginal, setLoadingOriginal] = useState(false);
    const [error, setError] = useState('');
    const [hasRunCurrentAnalysis, setHasRunCurrentAnalysis] = useState(false); //sets flag to check if current analysis has been run

    useEffect(() => {
        fetchGroups();
        console.log('fetchGroups called at:', new Date().toISOString());
    }, []);

    useEffect(() => {
    if (groups.length > 0) {
        // Fetch all original NPV data once groups are loaded
        fetchAllOriginalNpv();
    }
    }, [groups]);

    // Fetch original NPV when groups are selected
    // Remove the current useEffect that calls fetchOriginalNpv()
    // Replace with this:
    useEffect(() => {
        setHasRunCurrentAnalysis(false);
        setNpvData([]);
        // No API call needed - just filter existing data
    }, [selectedGroups]);

    const fetchGroups = async () => {
        try {
            const response = await FlowireAPI.authGet(
                '/api/getgroups',
                getIdTokenClaims,
                isAuthenticated
            );
            
            console.log('Groups API response:', response);
            
            let groupsArray;
            if (Array.isArray(response)) {
                groupsArray = response;
            } else if (response && Array.isArray(response.groups)) {
                groupsArray = response.groups;
            } else {
                console.error('Groups API returned unexpected format:', response);
                setError('Invalid response format from groups API');
                return;
            }
            
            const groupOptions = groupsArray.map(group => ({
                value: group.uuid,
                label: group.name || `Group ${group.uuid.substring(0, 8)}`
            }));
            
            setGroups(groupOptions);
            console.log('Processed group options:', groupOptions);
            
        } catch (error) {
            console.error('Error fetching groups:', error);
            setError('Failed to load groups. Please refresh the page and try again.');
        }
    };

    const fetchAllOriginalNpv = async () => {
        console.log('Fetching all original NPV data on mount');
        setLoadingOriginal(true);
        setError(''); // Clear any previous errors
        try {
            const payload = {
                group_uuids: [] // Empty array to fetch all original NPV data
            };

            console.log('Fetching all original stored NPV data:', payload);

            const results = await FlowireAPI.authPost(
                '/api/econ/original/npv',
                payload,
                getIdTokenClaims,
                isAuthenticated,
                (error) => {
                    throw new Error(`HTTP error! status: ${error}`);
                }
            );

            console.log('Received all original NPV results:', results);

            results.forEach(result => {
                const group = groups.find(g => g.value === result.group_uuid);
                const groupName = group ? group.label : 'Unknown Group';
                console.log(`Model: ${result.name}, Group: ${result.group_uuid} (${groupName})`);
            });
            
            if (Array.isArray(results) && results.length > 0) {
                const validResults = results.filter(result => {
                    const isValid = result && 
                        typeof result.npvCalculated === 'number' &&
                        result.name;
                    
                    if (!isValid) {
                        console.warn('Invalid original NPV result:', result);
                    }
                    return isValid;
                });
                
                console.log(`Processed ${validResults.length} valid original NPV results from all models`);
                setOriginalNpvData(validResults);
            } else {
                console.log('No original NPV results returned from organization');
                setOriginalNpvData([]);
            }
            
        } catch (error) {
            console.error('Error fetching all original NPV data:', error);
            
            // Set a more specific error message
            if (error.message.includes('404')) {
                console.warn('Original NPV endpoint not found - may need to implement backend');
            } else {
                console.warn('Could not fetch all original NPV data:', error.message);
            }
            
            setOriginalNpvData([]);
        } finally {
            setLoadingOriginal(false);
        }
    };

    const simulateAssumptions = async () => {
        if (electricityCost < 0) {
            setError('Electricity cost cannot be negative');
            return;
        }
        
        if (interestRate < 0 || interestRate > 100) {
            setError('Interest rate must be between 0 and 100%');
            return;
        }
        
        if (years < 1 || years > 50) {
            setError('Analysis period must be between 1 and 50 years');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const payload = {
                electricity: electricityCost,
                interest: interestRate / 100,
                years: years,
                group_uuids: selectedGroups.length > 0 
                    ? selectedGroups.map(group => group.value)
                    : [] // Empty array = analyze all (just like table shows all)
            };

            console.log('Sending payload:', payload);

            const results = await FlowireAPI.authPost(
                '/api/econ/assumptions/npv',
                payload,
                getIdTokenClaims,
                isAuthenticated,
                (error) => {
                    throw new Error(`HTTP error! status: ${error}`);
                }
            );

            console.log('Received results:', results);
            
            if (!Array.isArray(results)) {
                throw new Error('Invalid response format from server');
            }
            
            if (results.length === 0) {
                setError('No valid process models found with economic data. Please ensure your selected models have been properly simulated with economic parameters.');
                setNpvData([]);
                return;
            }
            
            const validResults = results.filter(result => {
                const hasRequiredFields = result && 
                    typeof result.npvCalculated === 'number' &&
                    Array.isArray(result.yearsTArray) &&
                    Array.isArray(result.discountedCashflowCalculated) &&
                    result.name;
                
                if (!hasRequiredFields) {
                    console.warn('Skipping invalid result:', result);
                }
                
                return hasRequiredFields;
            });
            
            if (validResults.length === 0) {
                setError('No valid economic data found in the selected process models. Please ensure your models have been properly simulated.');
                setNpvData([]);
                return;
            }
            
            if (validResults.length < results.length) {
                setError(`Warning: ${results.length - validResults.length} model(s) had incomplete economic data and were excluded from analysis.`);
            }
            
            setNpvData(validResults);
            setHasRunCurrentAnalysis(true); // Set flag indicating current analysis has been run
            // Note: originalNpvData is preserved for comparison
            
        } catch (error) {
            console.error('Error during simulation:', error);
            
            if (error.message.includes('404')) {
                setError('No process models found for the selected groups.');
            } else if (error.message.includes('401') || error.message.includes('403')) {
                setError('Authentication error. Please refresh the page and try again.');
            } else if (error.message.includes('500')) {
                setError('Server error during calculation. Please try again or contact support.');
            } else {
                setError(`Analysis failed: ${error.message || 'Please try again.'}`);
            }
        } finally {
            setLoading(false);
        }
    };

    // Filter NPV data based on selected groups
    const getFilteredNpvData = () => {
        if (!npvData || npvData.length === 0) return [];
        
        const selectedGroupUuids = selectedGroups.map(group => group.value);
        if (selectedGroupUuids.length === 0) return [];
        
        return npvData.filter(data => 
            selectedGroupUuids.includes(data.group_uuid)
        );
    };

    // Create original NPV summary table
    const createOriginalNpvSummary = () => {
        if (!originalNpvData || originalNpvData.length === 0) {
            return null;
        }

         const dataToShow = selectedGroups.length > 0 
        ? originalNpvData.filter(data => 
            selectedGroups.some(group => group.value === data.group_uuid)
          )
        : originalNpvData; // Show all if no groups selected

        if (dataToShow.length === 0) {
            return null;
        }

        return (
            <Box mb={6}>
                <HStack mb={4}>
                    <Heading size="md">Original NPV Values</Heading>
                    <Badge colorScheme="blue" variant="subtle">Current Models</Badge>
                </HStack>
                <TableContainer>
                    <Table variant="simple" size="md">
                        <Thead bg={useColorModeValue('blue.50', 'blue.800')}>
                            <Tr>
                                <Th>Process Model</Th>
                                <Th isNumeric>NPV ($)</Th>
                                <Th textAlign="center">Status</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {dataToShow.map((data, index) => (
                                <Tr key={index}>
                                    <Td fontWeight="medium">
                                        {data.name || `Model ${index + 1}`}
                                    </Td>
                                    <Td 
                                        isNumeric
                                        color={(data.npvCalculated || 0) >= 0 ? 'green.500' : 'red.500'}
                                        fontWeight="bold"
                                    >
                                        {(data.npvCalculated || 0).toLocaleString('en-US', {
                                            minimumFractionDigits: 0,
                                            maximumFractionDigits: 0
                                        })}
                                    </Td>
                                    <Td 
                                        textAlign="center"
                                        color={(data.npvCalculated || 0) >= 0 ? 'green.500' : 'red.500'}
                                        fontWeight="bold"
                                    >
                                        {(data.npvCalculated || 0) >= 0 ? 'Profitable' : 'Unprofitable'}
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    const createSummaryTable = () => {
        const filteredData = getFilteredNpvData();
        
        if (!filteredData || filteredData.length === 0) {
            return null;
        }

        return (
            <Box>
                <HStack mb={4}>
                    <Heading size="md">NPV with Modified Assumptions</Heading>
                    <Badge colorScheme="green" variant="subtle">Analysis Results</Badge>
                </HStack>
                <TableContainer>
                    <Table variant="simple" size="md">
                        <Thead bg={useColorModeValue('gray.50', 'gray.700')}>
                            <Tr>
                                <Th>Process Model</Th>
                                <Th isNumeric>NPV ($)</Th>
                                <Th textAlign="center">Status</Th>
                                {originalNpvData.length > 0 && <Th isNumeric>Change from Original</Th>}
                            </Tr>
                        </Thead>
                        <Tbody>
                            {filteredData.map((data, index) => {
                                // Find corresponding original NPV
                                const originalData = originalNpvData.find(orig => orig.name === data.name);
                                const originalNpv = originalData ? originalData.npvCalculated : null;
                                const change = originalNpv !== null ? (data.npvCalculated || 0) - originalNpv : null;
                                const changePercent = originalNpv !== null && originalNpv !== 0 ? ((change / originalNpv) * 100) : null;

                                return (
                                    <Tr key={index}>
                                        <Td fontWeight="medium">
                                            {data.name || `Model ${index + 1}`}
                                        </Td>
                                        <Td 
                                            isNumeric
                                            color={(data.npvCalculated || 0) >= 0 ? 'green.500' : 'red.500'}
                                            fontWeight="bold"
                                        >
                                            {(data.npvCalculated || 0).toLocaleString('en-US', {
                                                minimumFractionDigits: 0,
                                                maximumFractionDigits: 0
                                            })}
                                        </Td>
                                        <Td 
                                            textAlign="center"
                                            color={(data.npvCalculated || 0) >= 0 ? 'green.500' : 'red.500'}
                                            fontWeight="bold"
                                        >
                                            {(data.npvCalculated || 0) >= 0 ? 'Profitable' : 'Unprofitable'}
                                        </Td>
                                        {originalNpvData.length > 0 && (
                                            <Td 
                                                isNumeric
                                                color={change > 0 ? 'green.500' : change < 0 ? 'red.500' : 'gray.500'}
                                                fontWeight="bold"
                                            >
                                                {change !== null ? (
                                                    <VStack spacing={0} align="end">
                                                        <Text>
                                                            {change >= 0 ? '+' : ''}{change.toLocaleString('en-US', {
                                                                minimumFractionDigits: 0,
                                                                maximumFractionDigits: 0
                                                            })}
                                                        </Text>
                                                        {changePercent !== null && (
                                                            <Text fontSize="xs" opacity={0.8}>
                                                                ({changePercent >= 0 ? '+' : ''}{changePercent.toFixed(1)}%)
                                                            </Text>
                                                        )}
                                                    </VStack>
                                                ) : 'N/A'}
                                            </Td>
                                        )}
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    return (
    <Box p={6} maxWidth="1600px" mx="auto">
        <VStack spacing={4} align="stretch" mb={8}>
            <Heading size="md">Economic Assumptions Analysis</Heading>
            <Text fontSize="sm" color="gray.600">
                Test different economic scenarios against your saved process models.
            </Text>
        </VStack>

        <div style={{ display: 'flex', gap: '30px', width: '100%' }}>
            {/* LEFT SIDE - Input Panel */}
            <div style={{
                width: '350px',
                flexShrink: 0,
                flexGrow: 0
            }}>
                <Box
                    bg={useColorModeValue('gray.50', 'gray.700')}
                    p={6}
                    borderRadius="md"
                    border="1px"
                    borderColor={useColorModeValue('gray.200', 'gray.600')}
                >
                    <Heading size="md" mb={4}>Economic Assumptions</Heading>

                    <VStack spacing={4} align="stretch">
                        <FormControl>
                            <FormLabel fontWeight="bold">Electricity Cost ($/MWh)</FormLabel>
                            <Input
                                type="number"
                                step="0.01"
                                value={electricityCost}
                                onChange={(e) => setElectricityCost(e.target.value)}
                                bg="white"
                                _dark={{ bg: 'white', color: 'black' }}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel fontWeight="bold">Interest Rate (%)</FormLabel>
                            <Input
                                type="number"
                                step="0.1"
                                value={interestRate}
                                onChange={(e) => setInterestRate(e.target.value)}
                                bg="white"
                                _dark={{ bg: 'white', color: 'black' }}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel fontWeight="bold">Analysis Period (Years)</FormLabel>
                            <Input
                                type="number"
                                min="1"
                                max="50"
                                value={years}
                                onChange={(e) => setYears(parseInt(e.target.value))}
                                bg="white"
                                _dark={{ bg: 'white', color: 'black' }}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel fontWeight="bold">Select Groups</FormLabel>
                            <Select
                                isMulti
                                value={selectedGroups}
                                onChange={setSelectedGroups}
                                options={groups}
                                placeholder="Choose process models..."
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        minHeight: '40px',
                                        backgroundColor: 'white'
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        backgroundColor: 'white'
                                    }),
                                    option: (base, state) => ({
                                        ...base,
                                        backgroundColor: state.isSelected ? '#3182ce' : state.isFocused ? '#e2e8f0' : 'white',
                                        color: state.isSelected ? 'white' : 'black'
                                    }),
                                    multiValue: (base) => ({
                                        ...base,
                                        backgroundColor: '#e2e8f0'
                                    }),
                                    multiValueLabel: (base) => ({
                                        ...base,
                                        color: 'black'
                                    })
                                }}
                            />
                        </FormControl>

                        {loadingOriginal && (
                            <Alert status="info" borderRadius="md">
                                <AlertIcon />
                                <Text fontSize="sm">Loading original NPV data...</Text>
                            </Alert>
                        )}

                        <Button
                            onClick={simulateAssumptions}
                            isDisabled={loading}
                            isLoading={loading}
                            loadingText="Analyzing..."
                            colorScheme="blue"
                            size="md"
                            width="full"
                        >
                            Run Analysis
                        </Button>

                        {error && (
                            <Alert status="error" borderRadius="md">
                                <AlertIcon />
                                <Text fontSize="sm">{error}</Text>
                            </Alert>
                        )}
                    </VStack>
                </Box>
            </div>

            {/* RIGHT SIDE - Results */}
            <div style={{
                width: 'calc(100% - 350px - 30px)',
                flexShrink: 0,
                flexGrow: 0
            }}>
                <Box>
                    <Heading size="md" mb={6}>Analysis Results</Heading>

                    {/* NPV Graph - Always visible with fixed dimensions */}
                    <Box mb={8}>
                        <Box
                            borderRadius="md"
                            border="1px"
                            borderColor={useColorModeValue('gray.200', 'gray.600')}
                            overflow="hidden"
                            width="100%"
                            height="600px"  // Increased from 500px
                            position="relative"
                        >
                            {(() => {
                                const filteredData = getFilteredNpvData();
                                const filteredOriginalData = selectedGroups.length > 0 
                                    ? originalNpvData.filter(data => 
                                        selectedGroups.some(group => group.value === data.group_uuid)
                                    )
                                    : originalNpvData;
                                const hasOriginalData = filteredOriginalData && filteredOriginalData.length > 0;
                                                                const hasNewData = filteredData && filteredData.length > 0;
                                
                                if (!hasOriginalData && !hasNewData) {
                                    return (
                                        <Box
                                            textAlign="center"
                                            p={10}
                                            bg={useColorModeValue('gray.50', 'gray.700')}
                                            width="100%"
                                            height="100%"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <Text color="gray.500" fontSize="lg">
                                                No data available for visualization. Select groups to see original NPV, then run analysis to compare.
                                            </Text>
                                        </Box>
                                    );
                                }

                                const traces = [];

                                // Add original data traces (in blue tones)
                                if (hasOriginalData) {
                                    filteredOriginalData.forEach((data, index) => {
                                        traces.push({
                                            x: data.yearsTArray || [],
                                            y: data.discountedCashflowCalculated || [],
                                            type: 'scatter',
                                            mode: 'lines+markers',
                                            name: `${data.name || `Model ${index + 1}`} (Original)`,
                                            line: { 
                                                width: 2, 
                                                dash: 'solid',
                                                color: `hsl(${210 + (index * 30)}, 70%, 50%)` // Blue tones
                                            },
                                            marker: { size: 4 }
                                        });
                                    });
                                }

                                // Add new analysis traces (in green/red tones)
                                if (hasNewData) {
                                    filteredData.forEach((data, index) => {
                                        traces.push({
                                            x: data.yearsTArray || [],
                                            y: data.discountedCashflowCalculated || [],
                                            type: 'scatter',
                                            mode: 'lines+markers',
                                            name: `${data.name || `Model ${index + 1}`} (Modified)`,
                                            line: { 
                                                width: 3, 
                                                dash: 'solid',
                                                color: `hsl(${120 + (index * 30)}, 70%, 45%)` // Green tones
                                            },
                                            marker: { size: 6 }
                                        });
                                    });
                                }

                                const titleText = hasOriginalData && hasNewData 
                                    ? 'NPV Comparison: Original vs Modified Assumptions'
                                    : hasNewData 
                                        ? 'NPV with Modified Assumptions'
                                        : 'Original NPV Values';

                                return (
                                    <Plot
                                        data={traces}
                                        layout={{
                                            width: 1000,  // Increased from 800
                                            height: 540,  // Increased from 440
                                            title: {
                                                text: titleText,
                                                font: { size: 16 }
                                            },
                                            xaxis: {
                                                title: 'Years',
                                                gridcolor: '#e6e6e6'
                                            },
                                            yaxis: {
                                                title: 'Cumulative Discounted Cash Flow ($)',
                                                gridcolor: '#e6e6e6',
                                                tickformat: ',.0f'
                                            },
                                            plot_bgcolor: '#f9f9f9',
                                            paper_bgcolor: 'white',
                                            margin: { t: 60, b: 60, l: 120, r: 160 }, // Increased right margin for legend
                                            showlegend: true,
                                            legend: {
                                                x: 1.02,  // Position outside the plot area on the right
                                                y: 1,     // Top of the plot area
                                                xanchor: 'left',  // Anchor to the left of the legend box
                                                yanchor: 'top',   // Anchor to the top of the legend box
                                                bgcolor: 'rgba(255,255,255,0.9)',
                                                bordercolor: '#ccc',
                                                borderwidth: 1
                                            }
                                        }}
                                        style={{ width: '100%', height: '100%' }}
                                        config={{ 
                                            displayModeBar: false,
                                            staticPlot: false 
                                        }}
                                    />
                                );
                            })()}
                        </Box>
                    </Box>

                    {/* ALL ANALYSIS RESULTS BELOW GRAPH */}
                    {/* Original NPV Summary - Show when groups are selected */}
                    {createOriginalNpvSummary()}

                    {/* Summary Table - Show when there's analysis data */}
                    {hasRunCurrentAnalysis && getFilteredNpvData().length > 0 && createSummaryTable()}
                </Box>
            </div>
        </div>
    </Box>
    );
};

export default Assumptions;